import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { Task } from '../../types/task';
import { tasksAtom } from '../v0/taskAtoms';
import { UserActiveTrackingState } from '../../types/timeTracking';
import { fetchApi } from '../../lib/apiClient';

// Stores user-set progress for tasks without startAt/cost
export const userSetTaskProgressAtom = atomWithStorage<{ [taskId: string]: number }>(
  'v3_userSetTaskProgress',
  {}
);

// Stores active ephemeral focus sessions with their start time and intended duration
export const ephemeralTaskSessionsAtom = atomWithStorage<{ [taskId: string]: { startTime: number; durationMs: number } }>(
  'v3_ephemeralTaskSessions',
  {}
);

// Tracks the ID of the task in playingTaskIdsAtom currently shown in header, or 'clock'
export const headerActiveDisplayAtom = atomWithStorage<string | 'clock'>(
  'v3_headerActiveDisplay',
  'clock'
);

// Current primarily focused task ID (could be one of the playing tasks or null)
export const focusedTaskIdAtom = atomWithStorage<string | null>('v3_focusedTaskId', null);

// Base atom for playing task IDs - exported for cleanup operations
export const basePlayingTaskIdsAtom = atomWithStorage<string[]>('v3_playingTaskIds_base', []);

// Derived atom for managing playing tasks and synchronizing header display
export const playingTaskIdsAtom = atom(
  (get) => get(basePlayingTaskIdsAtom),
  (get, set, newPlayingTaskIdsArg: string[] | ((prev: string[]) => string[])) => {
    const oldPlayingTaskIds = get(basePlayingTaskIdsAtom);
    const newPlayingTaskIds = typeof newPlayingTaskIdsArg === 'function'
      ? newPlayingTaskIdsArg(oldPlayingTaskIds)
      : newPlayingTaskIdsArg;

    set(basePlayingTaskIdsAtom, newPlayingTaskIds);

    const currentHeaderDisplay = get(headerActiveDisplayAtom);
    const currentFocusedTask = get(focusedTaskIdAtom);

    // If the active header display was a task ID and it's no longer in the new playing tasks
    if (currentHeaderDisplay !== 'clock' && !newPlayingTaskIds.includes(currentHeaderDisplay)) {
      set(headerActiveDisplayAtom, newPlayingTaskIds.length > 0 ? newPlayingTaskIds[0] : 'clock');
    }
    // If header was clock, no tasks were playing, and now tasks are added, switch to the first new task
    else if (currentHeaderDisplay === 'clock' && oldPlayingTaskIds.length === 0 && newPlayingTaskIds.length > 0) {
      set(headerActiveDisplayAtom, newPlayingTaskIds[0]);
    }
    // If all tasks are removed and header was showing a task, switch to clock
    else if (newPlayingTaskIds.length === 0 && currentHeaderDisplay !== 'clock') {
      set(headerActiveDisplayAtom, 'clock');
    }

    // If the focused task is no longer in playing tasks, clear focus
    if (currentFocusedTask && !newPlayingTaskIds.includes(currentFocusedTask)) {
      set(focusedTaskIdAtom, null);
    }

    // Manage ephemeralTaskSessionsAtom
    const tasks = get(tasksAtom);
    const userProgressMap = get(userSetTaskProgressAtom);
    const currentEphemeralSessions = get(ephemeralTaskSessionsAtom);
    let updatedEphemeralSessions = { ...currentEphemeralSessions };
    let sessionsChanged = false;

    // Process removed tasks
    oldPlayingTaskIds.forEach(taskId => {
      if (!newPlayingTaskIds.includes(taskId)) {
        if (updatedEphemeralSessions[taskId]) {
          delete updatedEphemeralSessions[taskId];
          sessionsChanged = true;
        }
      }
    });

    // Process added tasks
    newPlayingTaskIds.forEach(taskId => {
      if (!oldPlayingTaskIds.includes(taskId)) {
        // Read fresh tasks state for each task verification to avoid timing issues
        const freshTasks = get(tasksAtom);
        const task = freshTasks.find(t => t.id === taskId);
        // Add to ephemeral session if:
        // 1. Task exists
        // 2. Task does not have startAt or cost (meaning it's eligible for manual/ephemeral progress)
        // 3. Task does not have a user-set progress already
        // 4. Task is not already in ephemeral session
        if (!task) {
          // Task not found yet (possibly due to timing), skip ephemeral session creation but allow playing
          console.warn(`Task ${taskId} not found in tasksAtom when adding to playing list, skipping ephemeral session creation`);
        } else if ((!task.startAt || !task.cost) && userProgressMap[taskId] === undefined && !updatedEphemeralSessions[taskId]) {
          updatedEphemeralSessions[taskId] = { startTime: Date.now(), durationMs: 10 * 60 * 1000 };
          sessionsChanged = true;
        }
      }
    });

    if (sessionsChanged) {
      set(ephemeralTaskSessionsAtom, updatedEphemeralSessions);
    }
  }
);

// Add task to playing list (simple version, starts a default 10-min session if eligible)
export const addPlayingTaskAtom = atom(
  null,
  (get, set, taskId: string) => {
    set(playingTaskIdsAtom, (prev) => {
      if (!prev.includes(taskId)) {
        return [...prev, taskId];
      }
      return prev;
    });
    set(headerActiveDisplayAtom, taskId); // Make it active in header
    set(focusedTaskIdAtom, taskId); // Also set as the globally focused task
  }
);

// New atom to start an ephemeral focus session with a specific duration
export const startEphemeralFocusSessionAtom = atom(
  null,
  (get, set, { taskId, durationMs }: { taskId: string; durationMs: number }) => {
    // Verify task exists before proceeding
    const tasks = get(tasksAtom);
    const task = tasks.find(t => t.id === taskId);
    if (!task) {
      console.warn(`Task ${taskId} not found when starting ephemeral focus session`);
      return;
    }

    // 1. Ensure task is in playingTaskIdsAtom
    set(playingTaskIdsAtom, (prev) => {
      if (!prev.includes(taskId)) {
        return [...prev, taskId];
      }
      return prev;
    });

    // 2. Set/update the ephemeral session with start time and new duration
    set(ephemeralTaskSessionsAtom, (prevSessions) => ({
      ...prevSessions,
      [taskId]: { startTime: Date.now(), durationMs },
    }));

    // 3. Clear any user-set progress for this task
    set(userSetTaskProgressAtom, (prevProgress) => {
      if (prevProgress[taskId] !== undefined) {
        const { [taskId]: _, ...rest } = prevProgress;
        return rest;
      }
      return prevProgress;
    });

    // 4. Set as focused task and header display
    set(headerActiveDisplayAtom, taskId);
    set(focusedTaskIdAtom, taskId);

    // 注意：移除了云端追踪调用，避免循环调用
    // 云端追踪将由 startTaskTrackingAtom 负责
  }
);

// Remove task from playing list
export const removePlayingTaskAtom = atom(
  null,
  (get, set, taskId: string) => {
    set(playingTaskIdsAtom, (prev) => prev.filter(id => id !== taskId));
    // focusedTaskIdAtom is handled by the playingTaskIdsAtom setter logic if needed
    // headerActiveDisplayAtom is handled by the playingTaskIdsAtom setter logic

    // 注意：移除了云端追踪调用，避免循环调用
    // 云端追踪将由 stopTaskTrackingAtom 负责
  }
);

// Set the globally focused task. If it's not playing, start playing it.
export const setFocusedTaskAtom = atom(
  null,
  async (get, set, taskId: string | null) => {
    const oldFocusedTaskId = get(focusedTaskIdAtom);
    const oldHeaderDisplay = get(headerActiveDisplayAtom);

    set(focusedTaskIdAtom, taskId);
    if (taskId) {
      // If setting a task as focused, ensure it's in playing list and active in header
      const currentPlayingIds = get(playingTaskIdsAtom);
      if (!currentPlayingIds.includes(taskId)) {
        set(playingTaskIdsAtom, [...currentPlayingIds, taskId]);
      }
      set(headerActiveDisplayAtom, taskId);
    } else {
      // If clearing focus, and no other tasks are playing, set header to clock
      const currentPlayingIds = get(playingTaskIdsAtom);
      if (currentPlayingIds.length === 0) {
        set(headerActiveDisplayAtom, 'clock');
      } else if (!currentPlayingIds.includes(get(headerActiveDisplayAtom) as string)) {
        // If the current header display is not valid anymore (e.g. focused task removed)
        // set it to the first playing task or clock.
        set(headerActiveDisplayAtom, currentPlayingIds.length > 0 ? currentPlayingIds[0] : 'clock');
      }
    }

    // 如果聚焦状态或头部显示发生变化，同步到云端
    const newFocusedTaskId = get(focusedTaskIdAtom);
    const newHeaderDisplay = get(headerActiveDisplayAtom);

    if (oldFocusedTaskId !== newFocusedTaskId || oldHeaderDisplay !== newHeaderDisplay) {
      set(updateCloudFocusStateAtom, {
        focusedTaskId: newFocusedTaskId,
        headerDisplay: newHeaderDisplay,
      });
    }
  }
);

// Atom to get displayed progress for a specific task
export const getDisplayedTaskProgressAtom = atom((get) => (taskId: string) => {
  const tasks = get(tasksAtom);
  const task = tasks.find((t) => t.id === taskId);

  if (!task) {
    return 0;
  }

  // 1. If task has startAt and cost, calculate time-based progress
  if (task.startAt && task.cost) {
    const startTime = new Date(task.startAt).getTime();
    const endTime = startTime + task.cost * 1000;
    const currentTime = new Date().getTime();

    if (currentTime < startTime) return 0;
    if (currentTime > endTime) return 100;
    const progress = ((currentTime - startTime) / (endTime - startTime)) * 100;
    return Math.max(0, Math.min(100, progress));
  }

  // 2. Check for user-set progress (manual override)
  const userProgressMap = get(userSetTaskProgressAtom);
  if (userProgressMap[taskId] !== undefined) {
    return userProgressMap[taskId];
  }

  // 3. If task has an active ephemeral session
  const ephemeralSessions = get(ephemeralTaskSessionsAtom);
  const session = ephemeralSessions[taskId];
  if (session) {
    const { startTime, durationMs } = session;
    const currentTime = new Date().getTime();
    const elapsedTime = currentTime - startTime;

    if (elapsedTime <= 0) return 0;
    if (elapsedTime >= durationMs) return 100;
    const progress = (elapsedTime / durationMs) * 100;
    return Math.max(0, Math.min(100, progress));
  }

  // 4. Default to 0%
  return 0;
});

// Atom to get remaining time for an ephemeral session
export const getEphemeralSessionRemainingTimeAtom = atom((get) => (taskId: string): number | null => {
  const sessions = get(ephemeralTaskSessionsAtom);
  const session = sessions[taskId];
  const playingIds = get(playingTaskIdsAtom);

  if (session && playingIds.includes(taskId)) {
    const { startTime, durationMs } = session;
    const currentTime = new Date().getTime();
    const endTime = startTime + durationMs;
    const remainingTime = endTime - currentTime;
    return remainingTime > 0 ? remainingTime : 0;
  }
  return null;
});

// Atom to update user-set progress for a task
export const updateUserSetTaskProgressAtom = atom(
  null,
  (get, set, { taskId, progress }: { taskId: string; progress: number }) => {
    const tasks = get(tasksAtom);
    const task = tasks.find((t) => t.id === taskId);

    if (task && !(task.startAt && task.cost)) {
      set(userSetTaskProgressAtom, (prev) => ({
        ...prev,
        [taskId]: Math.max(0, Math.min(100, progress)),
      }));
      // When user manually sets progress, clear any ephemeral session for that task
      set(ephemeralTaskSessionsAtom, (prevSessions: { [key: string]: { startTime: number; durationMs: number } }) => {
        if (prevSessions[taskId]) {
          const { [taskId]: _, ...rest } = prevSessions;
          return rest;
        }
        return prevSessions;
      });
    }
  }
);

// Removed: focusedTaskIdAtom (original simple version, now more complex)
// Removed: focusTaskProgressAtom (global progress, replaced by per-task logic)
// Removed: showFocusTaskCardAtom
// Removed: toggleFocusTaskCardAtom
// Removed: calculateTaskProgressAtom (renamed and enhanced to getDisplayedTaskProgressAtom)

// The old playingTaskIdsAtom logic is now part of the enhanced playingTaskIdsAtom.
// The old addPlayingTaskAtom and removePlayingTaskAtom are rewritten.
// The old setFocusedTaskAtom is rewritten.

// Global state synchronization atom - monitors tasksAtom changes and cleans up focus state
export const syncFocusStateWithTasksAtom = atom(
  null,
  (get, set) => {
    const tasks = get(tasksAtom);
    const playingTaskIds = get(basePlayingTaskIdsAtom);
    const ephemeralSessions = get(ephemeralTaskSessionsAtom);
    const userProgress = get(userSetTaskProgressAtom);
    const focusedTaskId = get(focusedTaskIdAtom);
    const headerDisplay = get(headerActiveDisplayAtom);

    // Get all valid task IDs (existing and not completed)
    const validTaskIds = new Set(tasks.filter(t => !t.done).map(t => t.id));

    // Clean up playingTaskIds - remove non-existent or completed tasks
    const validPlayingTaskIds = playingTaskIds.filter(id => validTaskIds.has(id));
    if (validPlayingTaskIds.length !== playingTaskIds.length) {
      set(basePlayingTaskIdsAtom, validPlayingTaskIds);
    }

    // Clean up ephemeralSessions - remove invalid tasks
    const validSessions = Object.fromEntries(
      Object.entries(ephemeralSessions).filter(([taskId]) => validTaskIds.has(taskId))
    );
    if (Object.keys(validSessions).length !== Object.keys(ephemeralSessions).length) {
      set(ephemeralTaskSessionsAtom, validSessions);
    }

    // Clean up userProgress - remove invalid tasks
    const validProgress = Object.fromEntries(
      Object.entries(userProgress).filter(([taskId]) => validTaskIds.has(taskId))
    );
    if (Object.keys(validProgress).length !== Object.keys(userProgress).length) {
      set(userSetTaskProgressAtom, validProgress);
    }

    // Clean up focusedTaskId if task doesn't exist or is completed
    if (focusedTaskId && !validTaskIds.has(focusedTaskId)) {
      set(focusedTaskIdAtom, null);
    }

    // Clean up headerDisplay if showing invalid task
    if (headerDisplay !== 'clock' && !validPlayingTaskIds.includes(headerDisplay)) {
      set(headerActiveDisplayAtom, validPlayingTaskIds.length > 0 ? validPlayingTaskIds[0] : 'clock');
    }
  }
);

// Cleanup expired focus state atom - removes sessions older than 24 hours
export const cleanupExpiredFocusStateAtom = atom(
  null,
  (get, set) => {
    const tasks = get(tasksAtom);
    const ephemeralSessions = get(ephemeralTaskSessionsAtom);

    // Clean up sessions older than 24 hours
    const now = Date.now();
    const validSessions = Object.fromEntries(
      Object.entries(ephemeralSessions).filter(([taskId, session]) => {
        const task = tasks.find(t => t.id === taskId);
        return task && !task.done && (now - session.startTime) < 24 * 60 * 60 * 1000;
      })
    );

    if (Object.keys(validSessions).length !== Object.keys(ephemeralSessions).length) {
      set(ephemeralTaskSessionsAtom, validSessions);
    }
  }
);

// ==================== 云同步相关原子 ====================

/**
 * 用户活跃追踪状态原子（从云端获取）
 */
export const userActiveTrackingAtom = atom<UserActiveTrackingState | null>(null);

/**
 * 云同步加载状态原子
 */
export const cloudSyncLoadingAtom = atom<boolean>(false);

/**
 * 云同步错误状态原子
 */
export const cloudSyncErrorAtom = atom<string | null>(null);

/**
 * 最后同步时间原子
 */
export const lastSyncTimeAtom = atom<string | null>(null);

/**
 * 开始任务追踪的原子（云端）
 */
export const startTaskTrackingAtom = atom(
  null,
  async (get, set, { taskId, durationMs }: { taskId: string; durationMs: number }) => {
    set(cloudSyncLoadingAtom, true);
    set(cloudSyncErrorAtom, null);

    try {
      // 1. 先更新本地状态（乐观更新）
      set(startEphemeralFocusSessionAtom, { taskId, durationMs });

      // 2. 调用云端API
      const response = await fetchApi('/api/time-tracking/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: { taskId, durationMs }
      });

      if (response.success) {
        set(userActiveTrackingAtom, response.data);
        set(lastSyncTimeAtom, new Date().toISOString());
      } else {
        throw new Error(response.message || '开始追踪失败');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '开始追踪失败';
      set(cloudSyncErrorAtom, errorMessage);
      console.error('开始任务追踪失败:', error);

      // 3. 回滚本地状态
      set(removePlayingTaskAtom, taskId);
    } finally {
      set(cloudSyncLoadingAtom, false);
    }
  }
);

/**
 * 停止任务追踪的原子（云端）
 */
export const stopTaskTrackingAtom = atom(
  null,
  async (get, set, taskId: string) => {
    set(cloudSyncLoadingAtom, true);
    set(cloudSyncErrorAtom, null);

    try {
      // 1. 先更新本地状态（乐观更新）
      set(removePlayingTaskAtom, taskId);

      // 2. 调用云端API
      const response = await fetchApi('/api/time-tracking/stop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: { taskId }
      });

      if (response.success) {
        set(userActiveTrackingAtom, response.data.userActiveState);
        set(lastSyncTimeAtom, new Date().toISOString());
      } else {
        throw new Error(response.message || '停止追踪失败');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '停止追踪失败';
      set(cloudSyncErrorAtom, errorMessage);
      console.error('停止任务追踪失败:', error);

      // 3. 回滚本地状态（重新添加到播放列表）
      set(playingTaskIdsAtom, (prev) => {
        if (!prev.includes(taskId)) {
          return [...prev, taskId];
        }
        return prev;
      });
    } finally {
      set(cloudSyncLoadingAtom, false);
    }
  }
);

/**
 * 同步用户追踪状态的原子
 */
export const syncUserTrackingStateAtom = atom(
  null,
  async (get, set) => {
    set(cloudSyncLoadingAtom, true);
    set(cloudSyncErrorAtom, null);

    try {
      // 1. 调用云端API获取状态
      const response = await fetchApi('/api/time-tracking/status', {
        method: 'GET'
      });

      if (response.success) {
        const cloudState = response.data as UserActiveTrackingState;
        set(userActiveTrackingAtom, cloudState);

        // 2. 同步本地状态
        const playingTaskIds = cloudState.activeSessions.map(s => s.taskId);
        set(basePlayingTaskIdsAtom, playingTaskIds);
        set(focusedTaskIdAtom, cloudState.focusedTaskId);
        set(headerActiveDisplayAtom, cloudState.headerDisplay);

        set(lastSyncTimeAtom, new Date().toISOString());
      } else {
        throw new Error(response.message || '同步状态失败');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '同步状态失败';
      set(cloudSyncErrorAtom, errorMessage);
      console.error('同步用户追踪状态失败:', error);
    } finally {
      set(cloudSyncLoadingAtom, false);
    }
  }
);

/**
 * 更新聚焦状态的原子（云端）
 */
export const updateCloudFocusStateAtom = atom(
  null,
  async (get, set, { focusedTaskId, headerDisplay }: {
    focusedTaskId?: string | null;
    headerDisplay?: string;
  }) => {
    set(cloudSyncLoadingAtom, true);
    set(cloudSyncErrorAtom, null);

    // 保存旧状态用于回滚
    const oldFocusedTaskId = get(focusedTaskIdAtom);
    const oldHeaderDisplay = get(headerActiveDisplayAtom);

    try {
      // 1. 先更新本地状态（乐观更新）
      if (focusedTaskId !== undefined) {
        set(focusedTaskIdAtom, focusedTaskId);
      }
      if (headerDisplay !== undefined) {
        set(headerActiveDisplayAtom, headerDisplay);
      }

      // 2. 调用云端API
      const response = await fetchApi('/api/time-tracking/focus', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: { focusedTaskId, headerDisplay }
      });

      if (response.success) {
        set(userActiveTrackingAtom, response.data);
        set(lastSyncTimeAtom, new Date().toISOString());
      } else {
        throw new Error(response.message || '更新聚焦状态失败');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '更新聚焦状态失败';
      set(cloudSyncErrorAtom, errorMessage);
      console.error('更新聚焦状态失败:', error);

      // 3. 回滚本地状态
      set(focusedTaskIdAtom, oldFocusedTaskId);
      set(headerActiveDisplayAtom, oldHeaderDisplay);
    } finally {
      set(cloudSyncLoadingAtom, false);
    }
  }
);
